import {
    NON_PLAYER_GENDER_WORK_OPTIONS,
    PreferredFoot
} from '../../../../utils/constants';

// Form field configurations for easy maintenance
export const FORM_SECTIONS = {
    PROFILE: 'profile',
    CLUB: 'club',
    AVAILABILITY: 'availability'
};

export const CONTRACT_STATUS_OPTIONS = [
    { value: 'UNSIGNED', label: 'Unsigned' },
    { value: 'CONTRACT', label: 'Contract' },
    { value: 'NON_CONTRACT', label: 'Non Contract' }
];

export const FIELD_VALIDATION_RULES = {
    firstName: {
        required: true,
        minLength: 2,
        maxLength: 50,
        pattern: /^[A-Za-z]+$/
    },
    lastName: {
        required: true,
        minLength: 2,
        maxLength: 50,
        pattern: /^[A-Za-z]+$/
    },
    bio: {
        minLength: 10,
        maxLength: 500
    },
    location: {
        minLength: 2,
        maxLength: 100,
        required: true,
        pattern: /^[A-Za-z]+$/
    },
    birthday: {
        required: true,
        noFuture: true,
        requiredMessage: 'Please enter your date of birth',
        maxDateMessage: 'Date of birth cannot be in the future',
        invalidMessage: 'Please enter a valid date'
    },
    height: {
        pattern: /^(?:[0-1]\.[0-9]{2}|2\.(?:[0-4][0-9]|50))$/,
        required: true
    },
    clubName: {
        minLength: 2,
        maxLength: 100,
        requiredWhenContractStatus: true,
        requiredWhenContractStatusMessage: 'Please select a football club'
    },
    teamName: {
        minLength: 2,
        maxLength: 100,
        requiredWhenClubSelected: true,
        requiredWhenClubSelectedMessage: 'Please select a team'
    },
    contractExpiry: {
        maxLength: 50
    },
    representedBy: {
        maxLength: 100
    },
    preferredFoot: {
        options: Object.keys(PreferredFoot) || [],
        required: true
    },
    contractStatus: {
        options: ['UNSIGNED', 'CONTRACT', 'NON_CONTRACT']
    },
    secondaryPositions: {
        maxItems: 3
    },
    nationalEligibility: {
        maxItems: 3
    },
    nonPlayerRole: {
        maxItems: 3,
        required: true,
        minItems: 1
    },
    position: {
        required: true
    },
    passports: {
        maxItems: 3
    },
    gender: {
        options:
            NON_PLAYER_GENDER_WORK_OPTIONS.map(option => option.value) || []
    }
};

export const FIELD_CONFIGS = {
    firstName: {
        type: 'text',
        label: 'First Name',
        required: true,
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.firstName
    },
    lastName: {
        type: 'text',
        label: 'Last Name',
        required: true,
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.lastName
    },
    preferredFoot: {
        type: 'dropdown',
        label: 'Preferred Foot',
        allowMultiple: false,
        section: FORM_SECTIONS.PROFILE,
        playerOnly: true,
        validation: FIELD_VALIDATION_RULES.preferredFoot
    },
    height: {
        type: 'text',
        label: 'Height',
        playerOnly: true,
        placeholder: 'Meters (e.g., 1.75)',
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.height
    },
    location: {
        type: 'text',
        label: 'Location',
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.location
    },
    position: {
        type: 'dropdown',
        label: 'Primary Position',
        allowMultiple: false,
        section: FORM_SECTIONS.PROFILE,
        playerOnly: true,
        required: true,
        validation: FIELD_VALIDATION_RULES.position
    },
    secondaryPositions: {
        type: 'dropdown',
        label: 'Secondary Position',
        allowMultiple: true,
        section: FORM_SECTIONS.PROFILE,
        playerOnly: true,
        validation: FIELD_VALIDATION_RULES.secondaryPositions
    },
    bio: {
        type: 'textarea',
        label: 'Bio',
        placeholder: 'Write about yourself (10-500 characters)',
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.bio
    },
    clubName: {
        type: 'text',
        label: 'Club Name',
        section: FORM_SECTIONS.CLUB,
        playerOnly: true,
        validation: FIELD_VALIDATION_RULES.clubName
    },
    teamName: {
        type: 'text',
        label: 'Team Name',
        section: FORM_SECTIONS.CLUB,
        validation: FIELD_VALIDATION_RULES.teamName
    },
    contractStatus: {
        type: 'radio',
        label: 'Contract Status',
        playerOnly: true,
        options: CONTRACT_STATUS_OPTIONS,
        section: FORM_SECTIONS.CLUB,
        validation: FIELD_VALIDATION_RULES.contractStatus
    },
    contractExpiry: {
        type: 'text',
        label: 'Contract Expiry Date',
        placeholder: 'e.g., July 2025 (Leave blank if not applicable)',
        section: FORM_SECTIONS.CLUB,
        playerOnly: true,
        validation: FIELD_VALIDATION_RULES.contractExpiry
    },
    nationalEligibility: {
        type: 'dropdown',
        label: 'National Eligibility',
        allowMultiple: true,
        playerOnly: true,
        section: FORM_SECTIONS.AVAILABILITY,
        validation: FIELD_VALIDATION_RULES.nationalEligibility
    },
    representedBy: {
        type: 'text',
        label: 'Represented by',
        placeholder: 'e.g., Manager/Agent (Leave blank if not applicable)',
        section: FORM_SECTIONS.AVAILABILITY,
        playerOnly: true,
        validation: FIELD_VALIDATION_RULES.representedBy
    },
    nonPlayerRole: {
        type: 'dropdown',
        label: 'Role/s',
        allowMultiple: true,
        section: FORM_SECTIONS.AVAILABILITY,
        nonPlayerOnly: true,
        validation: FIELD_VALIDATION_RULES.nonPlayerRole
    },
    gender: {
        type: 'text',
        label: 'I work in…',
        section: FORM_SECTIONS.AVAILABILITY,
        nonPlayerOnly: true,
        validation: FIELD_VALIDATION_RULES.gender
    },
    birthday: {
        type: 'date',
        label: 'Date of Birth',
        required: true,
        playerOnly: true,
        section: FORM_SECTIONS.PROFILE,
        validation: FIELD_VALIDATION_RULES.birthday
    }
};

export const VALIDATION_MESSAGES = {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    minLength: 'This field must be at least {min} characters',
    maxLength: 'This field must be no more than {max} characters',
    invalidNumber: 'Please enter a valid number',
    invalidDate: 'Please enter a valid date',
    futureDate: 'Date cannot be in the future',
    maxItems: 'You can select maximum {max} items',
    minDate: 'This field is too far in the past',
    maxDate: 'This field cannot be in the future',
    invalidPattern: 'Please enter a valid format'
};

// Field-specific validation messages for better UX
export const FIELD_SPECIFIC_MESSAGES = {
    birthday: {
        required: 'Please enter your date of birth',
        maxDate: 'Date of birth cannot be in the future',
        invalidDate: 'Please enter a valid birth date'
    },
    height: {
        required: 'Please enter your height',
        invalidPattern: 'Height must be in format x.xx (e.g. 1.83)',
        invalidNumber: 'Height must be a valid number'
    },
    firstName: {
        required: 'Please enter your first name',
        minLength: 'First name must be at least {min} characters'
    },
    lastName: {
        required: 'Please enter your last name',
        minLength: 'Last name must be at least {min} characters'
    }
};

export const FORM_LAYOUT = {
    profile: {
        title: 'Profile',
        fields: [
            ['firstName', 'lastName'],
            ['preferredFoot', 'height'], // playerOnly row
            ['birthday', 'location'],
            ['position', 'secondaryPositions'], // playerOnly row
            ['bio']
        ]
    },
    club: {
        title: 'Club',
        fields: [
            ['contractStatus'],
            ['clubSelection'], // custom component
            ['teamSelection'], // conditional
            ['contractExpiry'] // playerOnly
        ]
    },
    availability: {
        title: 'Availability',
        fields: [
            ['nationalEligibility'],
            ['passports'],
            ['representedBy'], // playerOnly
            ['openToTrials'], // playerOnly switch
            ['nonPlayerRole'] // nonPlayerOnly
        ]
    }
};

// Helper function to get field names by user type
export const getFieldsByUserType = userType => {
    const playerOnlyFields = Object.keys(FIELD_CONFIGS).filter(
        fieldName => FIELD_CONFIGS[fieldName].playerOnly
    );

    const nonPlayerOnlyFields = Object.keys(FIELD_CONFIGS).filter(
        fieldName => FIELD_CONFIGS[fieldName].nonPlayerOnly
    );

    const commonFields = Object.keys(FIELD_CONFIGS).filter(
        fieldName =>
            !FIELD_CONFIGS[fieldName].playerOnly &&
            !FIELD_CONFIGS[fieldName].nonPlayerOnly
    );

    if (userType === 'PLAYER') {
        return [...commonFields, ...playerOnlyFields];
    } else if (userType === 'NON_PLAYER') {
        return [...commonFields, ...nonPlayerOnlyFields];
    }

    return Object.keys(FIELD_CONFIGS);
};

export default {
    FORM_SECTIONS,
    CONTRACT_STATUS_OPTIONS,
    FIELD_CONFIGS,
    VALIDATION_MESSAGES,
    FIELD_SPECIFIC_MESSAGES,
    FORM_LAYOUT,
    getFieldsByUserType
};
